import React, { Suspense, useEffect, useMemo, useReducer, useState, useRef, useCallback } from 'react';
import PageConfig from '@/component/layout/PageConfig.tsx';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CloseButton from '@/component/button/CloseButton.tsx';
import useQueryGetUserDetail from '@/api/auth/getUser.ts';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { AlertStatus, DeviceStatus, IUser } from '@/api/user/getUsers.ts';
import useIsMount from '@/util/hook/useIsMount.ts';
import { createPortal } from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import HomeIcon from '@/assets/ic_map_house.svg?react';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetUserDailyTimeline from '@/api/user/getUserDailyTimeline.ts';
import PositionTimelinePanel from '@/pages/users/detail/PositionTimelinePanel.tsx';
import {
    formatClock,
    hasTimelineLocation,
    initialPlayback,
    pathOf,
    playbackReducer,
    sortTimeline,
    summarizeTimeline,
    timelineKindColor,
    timelineKindLabel,
    unavailableLabel
} from '@/util/positionTimeline.ts';
import useMonthAvailabilityView from '@/util/hook/useMonthAvailabilityView.ts';
import AvailabilityCalendarGrid from '@/pages/users/detail/AvailabilityCalendarGrid.tsx';
import { format, eachDayOfInterval, endOfMonth, startOfMonth, subDays, addDays } from 'date-fns';
import { IUserBbox } from '@/api/user/getUsersBbox';
import { useTranslation } from 'react-i18next';
import UnifiedMap, { UnifiedMapRef } from '@/component/map/unified/UnifiedMap';
import UnifiedMarker from '@/component/map/unified/UnifiedMarker';
import UnifiedCircle from '@/component/map/unified/UnifiedCircle';
import UnifiedPolyline from '@/component/map/unified/UnifiedPolyline';
import UnifiedInfoWindow from '@/component/map/unified/UnifiedInfoWindow';

import { useMapType } from '@/component/map/MapContext';
import { useAppLocale } from '@/locales/locale';
import { reverseGeocode } from '@/component/map/unified/UnifiedGeocoder';

// 커스텀 달력 컴포넌트
const CustomDatePicker: React.FC<{
    value: string;
    onChange: (date: string) => void;
    userID: number;
}> = ({ value, onChange, userID }) => {
    const { t } = useTranslation();
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarPosition, setCalendarPosition] = useState<{ top: number; left: number } | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const buttonRef = useRef<HTMLButtonElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    // 다른 달로 넘겨도 그 달의 기록이 올 때까지는 직전 달을 그대로 보여 준다 (viewMonth)
    const {
        viewMonth,
        availability,
        isLoading: isLoadingAvailability,
        isError: availabilityError,
        isBusy
    } = useMonthAvailabilityView(userID, currentMonth, showCalendar);

    const calendarDates = useMemo(() => {
        const start = startOfMonth(viewMonth);
        const end = endOfMonth(viewMonth);
        const firstWeek = subDays(start, start.getDay());
        const lastWeek = addDays(end, 6 - end.getDay());

        return eachDayOfInterval({ start: firstWeek, end: lastWeek });
    }, [viewMonth]);

    const goToPreviousMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    const handleSelect = (dateString: string) => {
        onChange(dateString);
        setShowCalendar(false);
    };

    const updateCalendarPosition = useCallback(() => {
        if (!buttonRef.current) return;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const calendarWidth = 280;
        const viewportPadding = 8;
        const maxLeft = Math.max(viewportPadding, window.innerWidth - calendarWidth - viewportPadding);
        const left = Math.min(Math.max(buttonRect.left, viewportPadding), maxLeft);

        setCalendarPosition({
            top: buttonRect.bottom + 4,
            left,
        });
    }, []);

    useEffect(() => {
        if (!showCalendar) return;

        updateCalendarPosition();

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (buttonRef.current?.contains(target) || calendarRef.current?.contains(target)) {
                return;
            }

            setShowCalendar(false);
        };

        window.addEventListener('resize', updateCalendarPosition);
        window.addEventListener('scroll', updateCalendarPosition, true);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('resize', updateCalendarPosition);
            window.removeEventListener('scroll', updateCalendarPosition, true);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar, updateCalendarPosition]);

    const toggleCalendar = () => {
        if (!showCalendar) {
            updateCalendarPosition();
        }

        setShowCalendar(prev => !prev);
    };

    const calendar = showCalendar && calendarPosition && typeof document !== 'undefined' ? createPortal(
        <div
            ref={calendarRef}
            className="member-route-calendar"
            style={{
                position: 'fixed',
                top: calendarPosition.top,
                left: calendarPosition.left,
                zIndex: 1000,
            }}
        >
            <div className="flex justify-between items-center mb-4">
                <button onClick={goToPreviousMonth} className="p-1 hover:bg-gray-100 rounded">
                    ←
                </button>
                <h3 className="font-medium">
                    {format(viewMonth, t('users.detail.mapPage.datePicker.monthFormat'))}
                </h3>
                <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded">
                    →
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
                {[t('common.dayOfWeek.sun'), t('common.dayOfWeek.mon'), t('common.dayOfWeek.tue'), t('common.dayOfWeek.wed'), t('common.dayOfWeek.thu'), t('common.dayOfWeek.fri'), t('common.dayOfWeek.sat')].map(day => (
                    <div key={day} className="text-center text-xs text-gray-500 py-1">
                        {day}
                    </div>
                ))}
            </div>

            <AvailabilityCalendarGrid
                dates={calendarDates}
                currentMonth={viewMonth}
                today={new Date()}
                selectedDate={value}
                isLoading={isLoadingAvailability}
                isBusy={isBusy}
                availability={availability}
                onSelect={handleSelect}
            />

            <div className="mt-3 pt-3 border-t text-xs">
                <div className="flex flex-wrap justify-between gap-x-2 gap-y-1">
                    <span className="text-black">● {t('users.detail.mapPage.datePicker.legend.dataExists')}</span>
                    <span className="text-amber-600">● {t('users.detail.mapPage.datePicker.legend.unavailableOnly')}</span>
                    <span className="text-gray-400">● {t('users.detail.mapPage.datePicker.legend.noData')}</span>
                </div>
            </div>

            {isLoadingAvailability && (
                <div className="mt-2 text-xs text-gray-500 text-center">
                    {t('users.detail.mapPage.datePicker.legend.loading')}
                </div>
            )}
            {availabilityError && !isLoadingAvailability && (
                <div className="mt-2 text-xs text-red-600 text-center">
                    {t('common.error')}
                </div>
            )}
        </div>,
        document.body,
    ) : null;

    return (
        <div className="member-route-datepicker">
            <button
                ref={buttonRef}
                onClick={toggleCalendar}
                className="member-route-datepicker__button"
            >
                <span>{t('users.detail.mapPage.viewDate')}</span>
                <strong>{value || t('users.detail.mapPage.datePicker.placeholder')}</strong>
                <i>⌄</i>
            </button>

            {calendar}
        </div>
    );
};

export interface IProps {}

const UserDetailMapPage: React.FC<IProps> = ({}) => {
    const { t } = useTranslation();
    const { intlLocale } = useAppLocale();
    const params = useParams();
    const userID = Number(params?.id);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDate = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
    const { data } = useQueryGetUserDetail(userID);
    const { data: me } = useQueryGetMe();
    const { data: dailyTimeline, isLoading: isLoadingPositions, isError, error, refetch: refetchPositions } = useQueryGetUserDailyTimeline(userID, selectedDate);
    const [userInfo, setUserInfo] = useState<any>(null);
    const { isMount } = useIsMount();
    const [showDailyTrack, setShowDailyTrack] = useState<boolean>(true);
    const { mapType } = useMapType();
    const mapRef = useRef<UnifiedMapRef>(null);

    // 위치 이력과 재생·선택 상태
    const timeline = useMemo(() => (isError ? [] : sortTimeline(dailyTimeline ?? [])), [isError, dailyTimeline]);
    const timelineSummary = useMemo(() => summarizeTimeline(timeline), [timeline]);
    const [playback, dispatchPlayback] = useReducer(playbackReducer, initialPlayback);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
    const isPlaying = playback.isPlaying;
    const activeItem = playback.activeIndex === null ? null : timeline[playback.activeIndex] ?? null;
    const activeKindLabel = activeItem ? timelineKindLabel(activeItem.kind, t) : null;

    const handleDateSelect = (date: string) => {
        const params = new URLSearchParams(searchParams);
        params.set('date', date);
        setSearchParams(params);
    };

    const positionsErrorMsg = useMemo(() => {
        if (isError && error instanceof Error) return error.message;
        if (isLoadingPositions) return t('common.loadingData');
        if (timeline.length === 0) return t('users.detail.mapPage.error.noDataToday');
        return '';
    }, [isError, error, isLoadingPositions, timeline, t]);

    useEffect(() => {
        if (!(isError && error instanceof Error) && isLoadingPositions) return;
        dispatchPlayback({ type: 'reset' });
    }, [isError, error, isLoadingPositions, timeline]);

    const getDelayBySpeed = (speed: number): number => {
        switch (speed) {
            case 1: return 1000;
            case 2: return 500;
            case 4: return 250;
            default: return 1000;
        }
    };

    const startPlayback = () => {
        dispatchPlayback({ type: 'toggle', length: timeline.length });
    };

    const changePlaybackSpeed = () => {
        setPlaybackSpeed((speed) => speed === 4 ? 1 : speed * 2);
    };

    useEffect(() => {
        if (!playback.isPlaying) return;

        const timer = window.setTimeout(() => {
            dispatchPlayback({ type: 'tick', length: timeline.length });
        }, getDelayBySpeed(playbackSpeed));

        return () => window.clearTimeout(timer);
    }, [playback.isPlaying, playback.activeIndex, playbackSpeed, timeline.length]);

    const selectTimelineIndex = (index: number) => {
        dispatchPlayback({ type: 'select', index, length: timeline.length });

        const item = timeline[index];
        if (item && hasTimelineLocation(item)) {
            mapRef.current?.panTo(item.location.latitude, item.location.longitude);
        }
    };

    const openUserInfo = async (info?: IMarker<IUser>) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            const convertToUserBbox = (users: IUser[]): IUserBbox[] => {
                return users.map(user => ({
                    id: user.id,
                    name: user.name,
                    location: user.location ? {
                        latitude: user.location.latitude,
                        longitude: user.location.longitude,
                        timestamp: user.location.timestamp,
                        accuracy: user.location.accuracy
                    } : undefined,
                    todayAlert: user.alertStatus || AlertStatus.NONE,
                    deviceStatus: user.device?.status || DeviceStatus.OFFLINE
                }));
            };

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo<IUserBbox> = {
                    ...info!,
                    address: 'Coordinates not available',
                    users: convertToUserBbox(info.users),
                };
                setUserInfo(infoWithoutAddress);
                return;
            }

            const address = await reverseGeocode(mapType, latitude, longitude);
            const infoWithAddress: IMarkerInfo<IUserBbox> = {
                ...info!,
                address,
                users: convertToUserBbox(info.users),
            };
            setUserInfo(infoWithAddress);
        }
    };

    const formatTimestamp = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleString(intlLocale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (error) {
            return timestamp;
        }
    };

    const getMarkerColor = (alertStatus: AlertStatus | undefined) => {
        let color = '#2966F4';
        switch (alertStatus) {
            case AlertStatus.EMERGENCY:
                color = '#FF003D';
                break;
            case AlertStatus.BIO:
                color = '#FF7D33';
                break;
            default:
                color = '#2966F4';
                break;
        }
        return color;
    };

    const marker = useMemo(() => {
        if (!data) return undefined;
        return {
            id: Number(params?.id),
            deviceStatus: data?.device?.status,
            location: {
                latitude: data?.location?.latitude,
                longitude: data?.location?.longitude,
                accuracy: data?.location?.accuracy,
                timestamp: data?.location?.timestamp,
            },
            type: MarketTYPE.SINGLE,
            alertStatus: data?.alertStatus as AlertStatus ?? AlertStatus.NONE,
            users: [data],
        };
    }, [data]);

    const dailyPositionPath = useMemo(() => pathOf(timeline), [timeline]);

    const polylineOptions = {
        strokeColor: '#2966F4',
        strokeOpacity: 0.8,
        strokeWeight: 3,
    };

    const mapOptions = {
        zoomControl: true,
        draggable: true,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        scrollwheel: true,
        scrollWheelZoom: true
    };

    const handleClose = () => {
        navigate(`/user?id=${userID}`);
    };

    const markerSize40 = { width: 40, height: 40 };
    const markerSize25 = { width: 25, height: 25 };
    const markerSize12 = { width: 12, height: 12 };
    const markerSize30 = { width: 30, height: 30 };

    return (
        <PageConfig showTopNav={true} showSideNav={true} mapScreen>
            <div className="member-route-map">
                <Suspense fallback={null}>
                    {isMount && data && (
                        <div className="member-route-map__canvas">
                            <UnifiedMap
                                ref={mapRef}
                                center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                zoom={15}
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                options={mapOptions}
                            >
                                {isMount && data?.location && (
                                    <>
                                        <UnifiedCircle
                                            center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            radius={data?.location.accuracy}
                                            options={{
                                                fillColor: '#78bfffcc',
                                                strokeColor: '#78bfffcc',
                                                strokeWeight: 1,
                                            }}
                                        />
                                        <UnifiedMarker
                                            position={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            icon={{
                                                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                    `
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                        <circle cx="25" cy="25" r="20" fill="${getMarkerColor(data?.alertStatus)}" stroke="white" stroke-width="3"/>
                                                    </svg>
                                                    `
                                                )}`,
                                                scaledSize: markerSize40,
                                            }}
                                            onClick={() => openUserInfo(marker)}
                                        />

                                        {isMount && data?.addressLatitude && data?.addressLongitude && (
                                            <UnifiedMarker
                                                position={{ lat: data?.addressLatitude ?? 0, lng: data?.addressLongitude ?? 0 }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        ReactDOMServer.renderToString(<HomeIcon />)
                                                    )}`,
                                                    scaledSize: markerSize25,
                                                }}
                                            />
                                        )}

                                        {showDailyTrack && dailyPositionPath.length > 1 && (
                                            <UnifiedPolyline
                                                path={dailyPositionPath}
                                                options={polylineOptions}
                                            />
                                        )}

                                        {showDailyTrack && !isPlaying && timeline.map((item, index) => (
                                            hasTimelineLocation(item) ? (
                                                <UnifiedMarker
                                                    key={`daily-pos-${index}`}
                                                    position={{ lat: item.location.latitude, lng: item.location.longitude }}
                                                    icon={{
                                                        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                            `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                                                <circle cx="8" cy="8" r="6" fill="${timelineKindColor(item.kind)}" stroke="white" stroke-width="2"/>
                                                            </svg>`
                                                        )}`,
                                                        scaledSize: markerSize12,
                                                    }}
                                                    opacity={0.8}
                                                    onClick={() => selectTimelineIndex(index)}
                                                />
                                            ) : null
                                        ))}

                                        {activeItem && hasTimelineLocation(activeItem) && (
                                            <UnifiedMarker
                                                key="playback-marker"
                                                position={{ lat: activeItem.location.latitude, lng: activeItem.location.longitude }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                                                            <circle cx="15" cy="15" r="10" fill="#FF5722" stroke="white" stroke-width="3"/>
                                                        </svg>`
                                                    )}`,
                                                    scaledSize: markerSize30,
                                                }}
                                                animation={isPlaying ? 'BOUNCE' : null}
                                            />
                                        )}
                                    </>
                                )}

                                {userInfo && (
                                    <UnifiedInfoWindow
                                        position={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                        onCloseClick={() => setUserInfo(undefined)}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                        </div>
                                    </UnifiedInfoWindow>
                                )}

                                {activeItem && hasTimelineLocation(activeItem) && (
                                    <UnifiedInfoWindow
                                        position={{ lat: activeItem.location.latitude, lng: activeItem.location.longitude }}
                                        onCloseClick={() => dispatchPlayback({ type: 'reset' })}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <p className="font-semibold mb-1">
                                                {isPlaying ? t('users.detail.mapPage.infoWindow.playback.title') : t('users.detail.mapPage.infoWindow.position.title')}{activeKindLabel && ` · ${activeKindLabel}`}
                                            </p>
                                            <p className="text-sm">{t('common.time')} {formatTimestamp(activeItem.timestamp)}</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.position.accuracy')} {activeItem.location.accuracy}m</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.playback.progress')} {(playback.activeIndex ?? 0) + 1}/{timeline.length}</p>
                                        </div>
                                    </UnifiedInfoWindow>
                                )}
                            </UnifiedMap>
                        </div>
                    )}
                </Suspense>

                <section className="member-route-console">
                    <div className="member-route-console__heading">
                        <div>
                            <span>LOCATION HISTORY</span>
                            <h1>{data?.name || t('common.loading')} <small>{t('users.detail.mapPage.route')}</small></h1>
                        </div>
                        <div className="member-route-console__count">
                            <strong>{timelineSummary.located.toLocaleString()}</strong>
                            <span>{t('users.detail.mapPage.collected')}</span>
                            {timelineSummary.unavailable > 0 && <em>{t('users.positionTimeline.unavailableCount', { value: timelineSummary.unavailable.toLocaleString() })}</em>}
                        </div>
                    </div>

                    <div className="member-route-console__controls">
                            <CustomDatePicker
                                value={selectedDate}
                                onChange={handleDateSelect}
                                userID={userID}
                            />
                            <button
                                className="member-route-control member-route-control--refresh"
                                onClick={() => refetchPositions()}
                            >
                                ↻ {t('common.button.refresh')}
                            </button>

                            {!isLoadingPositions && timeline.length > 0 && (
                                <>
                                    <button
                                        className={`member-route-control member-route-control--play ${isPlaying ? 'is-playing' : ''}`}
                                        onClick={startPlayback}
                                    >
                                        {isPlaying ? '■' : '▶'} {isPlaying ? t('common.button.stop') : t('common.button.play')}
                                    </button>

                                    <button
                                        className="member-route-control"
                                        onClick={changePlaybackSpeed}
                                    >
                                        {t('users.detail.mapPage.playbackSpeed', { speed: playbackSpeed })}
                                    </button>

                                    {playback.activeIndex !== null && (
                                        <div className="member-route-console__progress">
                                            {playback.activeIndex + 1}/{timeline.length}
                                        </div>
                                    )}
                                </>
                            )}

                            {!isLoadingPositions && timeline.length > 0 && (
                                <button
                                    className={`member-route-control member-route-control--route ${showDailyTrack ? 'is-active' : ''}`}
                                    onClick={() => setShowDailyTrack(!showDailyTrack)}
                                >
                                    {showDailyTrack ? t('common.button.hideRoute') : t('common.button.showRoute')}
                                </button>
                            )}
                    </div>
                    <div className="member-route-console__legend">
                        <span><i className="is-current" />{t('locationHistory.info.currentLocation')}</span>
                        <span><i className="is-home" />{t('users.detail.mapPage.legend.home')}</span>
                        <span><i className="is-route" />{t('users.detail.mapPage.route')}</span>
                        <span><i className="is-emergency" />{timelineKindLabel('emergency', t)}</span>
                        <span><i className="is-fall" />{timelineKindLabel('fall', t)}</span>
                        <span><i className="is-bio" />{timelineKindLabel('bio', t)}</span>
                        <span><i className="is-emergencyTest" />{timelineKindLabel('emergencyTest', t)}</span>
                    </div>
                </section>

                <CloseButton
                    className="member-route-map__close"
                    onClick={handleClose}
                />

                {!isLoadingPositions && timeline.length > 0 && (
                    <PositionTimelinePanel
                        items={timeline}
                        activeIndex={playback.activeIndex}
                        onSelect={selectTimelineIndex}
                    />
                )}

                {activeItem && !hasTimelineLocation(activeItem) && (
                    <div className="member-route-moment" role="status">
                        <i />
                        <span>{formatClock(activeItem.timestamp)} · {unavailableLabel(activeItem.unavailableReason, t)}</span>
                    </div>
                )}

                {isLoadingPositions && (
                    <div className="member-route-toast">
                        <i /> {t('common.loadingData')}
                    </div>
                )}

                {!isLoadingPositions && positionsErrorMsg && (
                    <div className="member-route-toast member-route-toast--error">
                        <div>{positionsErrorMsg}</div>
                        <button
                            onClick={() => refetchPositions()}
                        >
                            {t('common.button.retry')}
                        </button>
                    </div>
                )}
            </div>
        </PageConfig>
    );
};

export default UserDetailMapPage;
